import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, fetchProjects, fetchServices } from '../lib/api';

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form State for Hero Webform
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot field
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl under indsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
      setErrorMessage(err.message || 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    async function loadData() {
      try {
        const [page, projList, servicesList] = await Promise.all([
          fetchPageBySlug('forside'),
          fetchProjects(),
          fetchServices()
        ]);
        setPageData(page);
        setProjects(projList.slice(0, 3)); // Show top 3 projects
        setServices(servicesList);
      } catch (err) {
        console.error('Error loading home data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Update document metadata
  useEffect(() => {
    if (pageData) {
      document.title = pageData.meta_title || 'Røjsmose Isolering ApS';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', pageData.meta_description || '');
      } else {
        const newMeta = document.createElement('meta');
        newMeta.name = 'description';
        newMeta.content = pageData.meta_description || '';
        document.head.appendChild(newMeta);
      }
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
      </div>
    );
  }

  const heroImage = pageData?.images?.[0] || 'https://assets.auxodev.dk/assets/4ff98460-cf40-4566-82c8-07bb5181a02e/rojsmose-new-hero-isolering.jpg';

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-brand-navy py-16 sm:py-24 lg:py-32 text-white">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/${heroImage.replace('https://assets.auxodev.dk/', '')}`}
            alt="Professionel isolering"
            className="h-full w-full object-cover object-left opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Text */}
            <div className="lg:col-span-7 space-y-6">
              <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl uppercase">
                Rør- & Ventilationsisolering til erhverv
              </h1>
              <p className="text-lg leading-8 text-white max-w-2xl">
                Røjsmose Isolering ApS leverer energibesparende rør- og ventilationsisolering samt ventilation til erhverv, industri, boligforeninger og entreprenører på hele Fyn.
              </p>
              
              {/* Trust badges or small bullet points */}
              <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-white">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-brand-orange" />
                  <span>Rør- & Ventilationsisolering</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-brand-orange" />
                  <span>Ventilation til erhverv</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-brand-orange" />
                  <span>40 års erfaring i branchen</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-brand-orange" />
                  <span>Stolt familievirksomhed</span>
                </div>
              </div>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <Link
                  to="/projekter"
                  className="rounded-full bg-white px-6 py-3.5 text-base font-bold text-brand-navy shadow-sm transition-all duration-200 hover:bg-slate-100 hover:scale-[1.02] active:scale-[0.98] uppercase"
                >
                  Se vores projekter
                </Link>
                <Link
                  to="/kontakt"
                  className="rounded-full bg-brand-orange px-6 py-3.5 text-base font-bold text-white shadow-sm transition-all duration-200 hover:bg-brand-orange-hover hover:scale-[1.02] active:scale-[0.98] uppercase flex items-center gap-2"
                >
                  <i className="fa-solid fa-envelope" />
                  Kontakt os
                </Link>
              </div>
            </div>

            {/* Right Column: Webform */}
            <div className="lg:col-span-5">
              <div className="rounded-none bg-white p-6 sm:p-8 text-slate-800 shadow-2xl border border-slate-100">
                <h2 className="text-2xl font-extrabold text-brand-navy uppercase tracking-tight">
                  Få et uforpligtende tilbud
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  Udfyld formularen, så vender vi tilbage til dig inden for 24 timer med et skarpt tilbud.
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  {/* Honeypot field - absolutely hidden */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="website_url">Hjemmeside URL</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label htmlFor="hero-name" className="block text-xs font-bold uppercase tracking-wider text-brand-navy">
                      Navn / Virksomhed <span className="text-brand-orange">*</span>
                    </label>
                    <input
                      type="text"
                      id="hero-name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="F.eks. Jensen Byg ApS"
                      className="mt-1 block w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-orange focus:outline-none focus:ring-0"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="hero-phone" className="block text-xs font-bold uppercase tracking-wider text-brand-navy">
                        Telefon <span className="text-brand-orange">*</span>
                      </label>
                      <input
                        type="tel"
                        id="hero-phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Tlf. nr."
                        className="mt-1 block w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-orange focus:outline-none focus:ring-0"
                      />
                    </div>
                    <div>
                      <label htmlFor="hero-email" className="block text-xs font-bold uppercase tracking-wider text-brand-navy">
                        E-mail <span className="text-brand-orange">*</span>
                      </label>
                      <input
                        type="email"
                        id="hero-email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="mail@firma.dk"
                        className="mt-1 block w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-orange focus:outline-none focus:ring-0"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="hero-description" className="block text-xs font-bold uppercase tracking-wider text-brand-navy">
                      Beskrivelse af opgaven <span className="text-brand-orange">*</span>
                    </label>
                    <textarea
                      id="hero-description"
                      name="description"
                      rows="3"
                      required
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Beskriv kort hvad der skal isoleres (f.eks. rør, ventilation eller erhvervsbyggeri)..."
                      className="mt-1 block w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-orange focus:outline-none focus:ring-0 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-full bg-brand-orange py-3 text-sm font-bold uppercase tracking-wider text-white shadow-md transition-all duration-200 hover:bg-brand-orange-hover hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fa-solid fa-spinner animate-spin" />
                        Sender...
                      </span>
                    ) : (
                      'Få et gratis tilbud'
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="rounded-md bg-emerald-50 p-3 border border-emerald-200">
                      <div className="flex gap-2 text-emerald-800 text-xs font-medium">
                        <i className="fa-solid fa-circle-check text-emerald-600 mt-0.5" />
                        <div>
                          <p className="font-bold uppercase text-emerald-700">Tak for din henvendelse!</p>
                          <p className="mt-0.5 text-emerald-600">Vi har modtaget din besked og kontakter dig hurtigst muligt.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="rounded-md bg-rose-50 p-3 border border-rose-200">
                      <div className="flex gap-2 text-rose-800 text-xs font-medium">
                        <i className="fa-solid fa-circle-exclamation text-rose-600 mt-0.5" />
                        <div>
                          <p className="font-bold uppercase text-rose-700">Der skete en fejl</p>
                          <p className="mt-0.5 text-rose-600">{errorMessage || 'Kunne ikke indsende formularen. Prøv venligst igen.'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction & Erhvervsfokus Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Erhvervsfokus Card */}
            <div className="lg:col-span-5 lg:order-1">
              <div className="rounded-none bg-brand-navy p-8 border border-brand-navy-light shadow-xl space-y-6 text-white">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange text-white">
                  <i className="fa-solid fa-briefcase text-xl" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-white">Fokus på erhverv & ventilation</h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Vi forstår de høje krav til teknisk isolering, rør, ventilation og dokumentation i erhvervssektoren og byggebranchen. Vi leverer altid præcist arbejde til tiden.
                </p>
                <ul className="space-y-3 text-sm text-slate-200">
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-brand-orange" aria-hidden="true" />
                    <span>Specialister i rør- & ventilationsisolering</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-brand-orange" aria-hidden="true" />
                    <span>Ventilation til erhverv, industri & entreprenører</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-solid fa-check text-brand-orange" aria-hidden="true" />
                    <span>40 års erfaring & stolt familievirksomhed</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Column: Text */}
            <div className="lg:col-span-7 lg:order-2 space-y-6">
              <h2 className="text-3xl font-extrabold tracking-tight text-brand-navy sm:text-4xl">
                Hvem er Røjsmose Isolering ApS?
              </h2>
              <div
                className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-4 prose-h2:font-extrabold prose-h2:text-brand-navy prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:uppercase prose-h2:tracking-tight prose-ul:list-disc prose-ul:pl-5"
                dangerouslySetInnerHTML={{ __html: pageData?.content || '' }}
              />
              <div className="pt-2">
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orange/90 text-white px-6 py-3 rounded-full font-bold uppercase tracking-wider text-sm transition-colors duration-200"
                >
                  <span>Læs mere om os</span>
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services/Kompetencer Section */}
      <section className="py-24 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20 flex flex-col items-center">
            <div className="flex h-12 w-12 items-center justify-center bg-brand-orange text-white rounded-full mb-4">
              <i className="fa-solid fa-screwdriver-wrench text-xl" aria-hidden="true" />
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight text-brand-navy sm:text-4xl">
              Vores specialiserede ydelser
            </h2>
            <p className="mt-4 text-lg text-slate-500">
              Vi tilbyder professionelle isoleringsløsninger tilpasset ethvert behov, med fokus på energibesparelse og kvalitet.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-x-12 gap-y-16">
            {services.slice(0, 3).map((service, index) => {
              return (
                <div
                  key={service.id}
                  className="relative flex flex-col items-center text-center px-4 w-full md:w-[calc(50%-2rem)] lg:w-[calc(33.333%-3rem)] max-w-sm"
                >
                  {/* Heading Link */}
                  <h3 className="text-lg font-semibold text-brand-navy mb-3 hover:text-brand-orange transition-colors">
                    <Link to={`/ydelser/${service.service_slug}`}>
                      {service.title}
                    </Link>
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {service.summary}
                  </p>

                  {/* Vertical divider for desktop (only between columns in the same row) */}
                  {(index + 1) % 3 !== 0 && index < Math.min(services.length, 3) - 1 && (
                    <div className="hidden lg:block absolute -right-6 top-8 bottom-8 w-[1px] bg-slate-200" />
                  )}

                  {/* Horizontal divider for mobile/tablet */}
                  {index < Math.min(services.length, 3) - 1 && (
                    <div className="block lg:hidden absolute -bottom-8 left-1/4 right-1/4 h-[1px] bg-slate-200" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      {projects.length > 0 && (
        <section className="py-20 bg-white">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
              <div>
                <h2 className="text-3xl font-extrabold tracking-tight text-brand-navy sm:text-4xl">
                  Udvalgte projekter
                </h2>
                <p className="mt-3 text-lg text-slate-600">
                  Se nogle af de opgaver, vi har løst for vores tilfredse kunder.
                </p>
              </div>
              <Link
                to="/projekter"
                className="mt-4 md:mt-0 inline-flex items-center gap-2 text-sm font-bold text-brand-orange hover:text-brand-orange-hover transition-colors"
              >
                Se alle projekter
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {projects.map((project) => {
                const projImage = project.images?.[0] || 'https://assets.auxodev.dk/assets/4ff98460-cf40-4566-82c8-07bb5181a02e/rojsmose-hero-isolering.jpg';
                return (
                  <Link
                    key={project.id}
                    to="/projekter"
                    className="group flex flex-col overflow-hidden rounded-none border border-slate-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-1"
                  >
                    <div className="relative h-48 overflow-hidden bg-slate-100 rounded-none">
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${projImage.replace('https://assets.auxodev.dk/', '')}`}
                        alt={project.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 rounded-none"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="text-lg font-bold text-brand-navy mb-2 group-hover:text-brand-orange transition-colors">
                        {project.title}
                      </h3>
                      <div
                        className="text-sm text-slate-600 line-clamp-3 mb-4"
                        dangerouslySetInnerHTML={{ __html: project.description || '' }}
                      />
                      <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between text-sm font-bold text-brand-orange">
                        <span>Se projektet</span>
                        <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" aria-hidden="true" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA Section */}
      <section className="relative overflow-hidden bg-brand-navy py-16 text-white border-t border-slate-800">
        {/* Dot pattern watermark on the right */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/12 pointer-events-none opacity-35" aria-hidden="true">
          <svg width="240" height="320" fill="none" viewBox="0 0 240 320" className="text-slate-300">
            <defs>
              <pattern id="dot-pattern-cta-right" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="3" cy="3" r="3" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="240" height="320" fill="url(#dot-pattern-cta-right)" />
          </svg>
        </div>

        {/* Dot pattern watermark on the left */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/12 pointer-events-none opacity-35" aria-hidden="true">
          <svg width="240" height="320" fill="none" viewBox="0 0 240 320" className="text-slate-300">
            <defs>
              <pattern id="dot-pattern-cta-left" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="3" cy="3" r="3" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="240" height="320" fill="url(#dot-pattern-cta-left)" />
          </svg>
        </div>

        <div className="relative z-10 mx-auto max-w-5xl px-6 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Skal vi hjælpe med jeres næste isoleringsprojekt?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-100">
            Kontakt os i dag for en uforpligtende samtale eller et professionelt tilbud på jeres opgave.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="rounded-full bg-brand-orange px-6 py-3.5 text-base font-bold text-white shadow-sm transition-all duration-200 hover:bg-brand-orange-hover hover:scale-[1.02] active:scale-[0.98]"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href="tel:+4542585848"
              className="border border-white bg-transparent px-6 py-3.5 rounded-full text-base font-bold text-white shadow-sm transition-all duration-200 hover:bg-transparent hover:border-white"
            >
              Ring til os: 42 58 58 48
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
